import type { Record as ImRecord, OrderedMap } from 'immutable';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import memoizeOne from 'memoize-one';
import type { SyntheticEvent } from 'react';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { datasetsSelectors, datasetsUtils } from 'app-components/flux/datasets';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { preferencesActions } from 'app-components/flux/preferences';
import { preferencesV2Actions, preferencesV2Selectors, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { ssoActions } from 'app-components/flux/sso';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { config, getBuildConfig, getEnvironmentConfig, platform, tracker } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import { DIALOG_TYPE_EMPTY_DIALOG } from 'components/Dialogs/EmptyDialog';
import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';
import { DIALOG_TYPE_REPORT_ERROR } from 'components/Dialogs/ReportErrorDialog';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import type { PopoverActions } from '@mui/material/Popover';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import NavIconProfile from 'assets/icons/nav-icons/profile.inline.svg';
import lifeHubLogoDark from 'assets/logos/quicken-lifehub-logo-dark.svg';
import lifeHubLogo from 'assets/logos/quicken-lifehub-logo.svg';
import simplifiLogoLight from 'assets/logos/simplifi-logo-light.svg';
import MiniDatasetPicker from 'components/MiniDatasetPicker';
import { makeStyles } from 'tss-react/mui';

import { achievementsActions } from 'app-components/flux/achievements';
import type { Meta } from 'app-components/flux/core/actionHelpers';
import type { Dataset } from 'app-components/flux/datasets/datasetsTypes';
import { milestonesActions, milestonesSelectors } from 'app-components/flux/milestones';
import type { Profile } from 'app-components/flux/profile/profileTypes';
import ConfusedStarImage from 'assets/illustrations/Confused-Star.png';
import Dump from 'components/Dump';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';
import { overviewLayout, overviewLayoutVersion } from 'containers/OverviewPage/config';
import { useAppSelector } from 'hooks/redux';
import { useNavigate } from 'react-router-dom';
import { useNavItemStyles } from '../commonNavStyles';

const getOrderedDatasets = memoizeOne((currentDataset, datasets) => {
  const data = [];
  if (currentDataset) data.push(currentDataset);
  datasets?.forEach((dataset: Dataset) => {
    if (dataset?.id && dataset?.id !== currentDataset?.id && data.length < 3) {
      data.push(dataset);
    }
  });
  return data;
});

const getFullName = (profile: Profile | undefined) => {
  if (!profile) {
    return '';
  }
  let fullName = '';
  if (profile.firstName && profile.lastName) {
    fullName = `${profile.firstName} ${profile.lastName}`;
  } else if (profile.primaryAddress && profile.primaryAddress.fullName) {
    fullName = profile.primaryAddress.fullName;
  }
  return fullName;
};

type Milestone = ImRecord<{
  id: string;
  imageLink: string;
}>;

const popOverWidth = 320;

const useStyles = makeStyles()((theme) => ({
  navText: {
    color: theme.palette.text.primary,
    marginLeft: 16,
    textAlign: 'left',
  },
  datasetSwitchingProgress: {
    marginTop: '30px',
  },
  datasetSwitchingContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  profilePopover: {
    minWidth: popOverWidth,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)}`,
  },
  profileHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'center',
  },
  profileDataset: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: '5px 8px 5px 8px',
  },
  rowWithIconSpan: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    alignItems: 'center',
  },
  leftSpan: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  leftText: {
    marginLeft: theme.spacing(1),
  },
  milestoneBadge: {
    height: 24,
    width: 24,
    marginRight: -8,
    position: 'relative',
  },
  datasetListItemDefault: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  dismissIcon: {
    color: theme.palette.icon.light,
    height: 20,
    width: 20,
    padding: theme.spacing(2),
    marginRight: `-${theme.spacing(1)}`, // Ignore padding of parent
    marginTop: `-${theme.spacing(2)}`, // Ignore padding of parent
  },
  lifeHubContainer: {
    borderRadius: 8,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)}`,
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
  },
  logo: {
    width: 24,
    height: 24,
  },
  lifeHubLogo: {
    width: 88,
    marginBottom: theme.spacing(1),
  },
  lifeHubButton: {
    marginLeft: -16, // Ignore its own padding
    marginBottom: -12, // Ignore its own padding
  },
  lifeHubLeft: {
    marginRight: theme.spacing(2),
    display: 'flex',
  },
  lifeHubMiddle: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    display: 'flex',
  },
  lifeHubDescription: {
    //  width: '70%',
  },
  lifeHubRight: {
    width: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  textPrimary: {
    color: theme.palette.text.primary,
  },
  textSecondary: {
    color: theme.palette.text.secondary,
  },
  linkIcon: {
    width: 20,
    height: 20,
    color: theme.palette.icon.light,
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  listItems: {
    padding: 0,
  },
  checkStyle: {
    color: theme.palette.primary.main,
  },
}));

function IconRowSpan({
  text,
  iconComponent,
  leftIcon,
}: {
  text: string;
  iconComponent?: any;
  leftIcon?: any;
}) {
  const { classes } = useStyles();
  return (
    <span className={classes.rowWithIconSpan}>
      <span className={classes.leftSpan}>
        {leftIcon}
        <Typography className={leftIcon ? classes.leftText : ''} variant='paragraphRegular'>
          {text}
        </Typography>
      </span>
      {iconComponent}
    </span>
  );
}

function AchievementsMenuItem({
  closeProfile,
}: {
  closeProfile: () => void;
}) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const recentlyEarned: Milestone[] = useAppSelector(
    (state) => milestonesSelectors.getUpNextMilestones(state).recentlyEarned,
  );

  const iconComponent = (
    <div>
      {recentlyEarned?.slice(0, 4)?.map((badge, index) => (
        <img
          src={badge.get('imageLink')}
          key={`${badge.get('id')}-profile-menu`}
          alt={'badge'}
          className={classes.milestoneBadge}
          style={{ zIndex: 100 - index }}
        />
      ))}
    </div>
  );

  return (
    <MenuItem
      id={'achievements-profile-menu'}
      onClick={() => {
        dispatch(achievementsActions.getAchievements());
        dispatch(milestonesActions.getMilestones());
        closeProfile();
        navigate('/achievements');
        tracker.track(tracker.events.achievementsPageView, { initiator: 'nav bar' });
      }}
    >
      <IconRowSpan text='Achievements' iconComponent={iconComponent} />
    </MenuItem>
  );
}

function ProfileSection({
  showHiddenMenu,
  dataset,
}: {
  showHiddenMenu: boolean;
  dataset: Dataset | undefined;
}) {
  const profile: Profile | undefined = useAppSelector(profileSelectors.getProfile);
  const subscription = useAppSelector(subscriptionsSelectors.getActiveSubscription);
  const fullName = getFullName(profile);
  const { classes } = useStyles();

  if (!profile) {
    return null;
  }
  return (
    <div className={classes.profileHeader}>
      <Dump obj={{ dataset, profile, subscription }} />
      <Typography variant='titleMedium' className={classes.textPrimary}>
        {fullName}
      </Typography>
      <Typography variant='paragraphRegular' className={classes.textSecondary}>
        {profile.username}
      </Typography>

      {showHiddenMenu && dataset && (
        <Stack id='my-account-dataset-id'>
          <Typography>
            user summary:
            <IconButton
              size='small'
              onClick={() => {
                const state = store?.getState();
                if (state) {
                  const transactions = transactionsSelectors.getResourcesById(state);
                  navigator.clipboard.writeText(
                    `email: ${profile?.username}\n` +
                      `user id: ${profile?.userId}\n` +
                      `dataset name: ${dataset?.name}\n` +
                      `dataset id: ${dataset?.id}\n` +
                      `transactions count: ${transactions?.size}\n` +
                      `environment: ${config.client?.hostname}/${config.client?.environment}\n` +
                      `version: ${config.client?.app_version}\n` +
                      `build: ${config.client?.build}/${config.client?.commit} (${config.client?.build_type})\n` +
                      `browser: ${platform.browser?.name} ${platform.browser?.version}\n` +
                      `OS: ${platform.os?.name} ${platform.os?.version}`,
                  );
                  dispatchSimpleNotification('User Summary is copied to clipboard');
                }
              }}
            >
              <FileCopyOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Typography>

          <Typography>
            user id: {profile.userId}
            <IconButton
              size='small'
              onClick={() => {
                navigator.clipboard.writeText(profile.userId || 'Unknown');
                dispatchSimpleNotification('User Id is copied to clipboard');
              }}
            >
              <FileCopyOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Typography>

          <Typography>
            dataset id: {dataset.id}
            <IconButton
              size='small'
              onClick={() => {
                navigator.clipboard.writeText(dataset.id || 'Unknown');
                dispatchSimpleNotification('Dataset Id is copied to clipboard');
              }}
            >
              <FileCopyOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Typography>
        </Stack>
      )}
    </div>
  );
}

function ProfileMenu({ hideFeatures }: { hideFeatures: boolean }) {
  const [profileButtonAnchorEl, setProfileButtonAnchorEl] = useState<null | Element>(null);
  const [showHiddenMenu, setShowHiddenMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const popoverActions = React.createRef<PopoverActions>();
  const { classes } = useStyles();
  const { classes: navItemClasses } = useNavItemStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const createDialog = (uiRootData: any) => dispatch(rootUiActions.createDialog(uiRootData));
  const dispatchGetSSOTokenAction = (data: undefined, meta: Meta) => dispatch(ssoActions.getSSOToken(data, meta));

  const feedbackEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('feedback'));
  const financesEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('finances'));
  const intercomEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('intercom'));
  const resetDashEnabled = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('resetDefaultDashboard'),
  );
  const profileHelpSupport = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('profileHelpSupport'),
  );

  const hasLifeHub = useAppSelector((state) => entitlementsSelectors.getActiveEntitlement(state, 'QEM_BASIC'));
  const lifeHubPref = useAppSelector((state) => preferencesV2Selectors.getPreferenceValue(state, 'sShowLifeHubBanner'));

  const authSessionAccessToken = useAppSelector((state) => authSelectors.getAuthSession(state).accessToken);
  const datasets: OrderedMap<string, Dataset> = useAppSelector(datasetsSelectors.getSortedDatasets);
  const datasetId: string = useAppSelector(authSelectors.getDatasetId);
  const dataset = datasets.get(datasetId);
  const overviewLayoutAcme = ImmutableList(overviewLayout);
  const data = getOrderedDatasets(dataset, datasets);

  const datasetsRefs = useRef(datasets);

  useEffect(() => {
    if (datasetsRefs.current !== datasets && Boolean(profileButtonAnchorEl)) {
      popoverActions.current?.updatePosition();
    }
  }, [datasets, popoverActions, profileButtonAnchorEl]);

  const closeProfile = () => {
    setProfileButtonAnchorEl(null);
    setShowHiddenMenu(false);
  };

  const myAccountClick = () => {
    tracker.track(tracker.events.myAccount, { location: 'nav menu' });
    closeProfile();
    window.open(`${getEnvironmentConfig().myaccount_url}?hide-nav&bearer=${authSessionAccessToken}`);
  };

  const logoutClick = () =>
    dispatch(authActions.authLogout({ reason: 'AUTH_LOGOUT_USER_INITIATED' }, { context: 'profile' }));

  const helpClick = () => {
    closeProfile();
    window.open('https://www.quicken.com/support');
  };

  const handleDatasetClick = (dataset: Dataset) => {
    if (datasetId !== dataset.id) {
      tracker.track(tracker.events.datasetChange, { location: 'nav menu' });
      setTimeout(
        () =>
          dispatch(authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset, userChoice: true }))),
        100,
      );
      closeProfile();
      createDialog(
        rootUiTypes.mkRootUiData({
          type: DIALOG_NOTIFICATION,
          allowNesting: false,
          props: ImmutableMap(
            mkNotificationDialogProps({
              buttonLabel: null,
              content: (
                <div className={classes.datasetSwitchingContent}>
                  <Typography variant='bodyRegular'>Switching to {dataset.name}</Typography>
                  <CircularProgress classes={{ root: classes.datasetSwitchingProgress }} size={50} />
                </div>
              ),
              dialogProps: {
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
              },
              title: '',
            }),
          ),
        }),
      );
    } else {
      setTimeout(closeProfile, 100); // the delay is only for feel to the user
    }
  };

  const handleProfileClose = () => {
    closeProfile();
  };

  const goToCommunity = () => {
    dispatch(rootUiActions.removeDialog('community-verify-email'));
    closeProfile();
    new Promise((resolve, reject) => {
      dispatchGetSSOTokenAction(undefined, {
        resolve,
        reject,
        axiosConfig: {
          params: {
            clientId: getBuildConfig().sso_client_id,
            redirect_uri: getBuildConfig().community_url,
          },
        },
      });
    })
      .then((response: { data: { errors: { code?: string }[]; redirectUri: string } }) => {
        if (response?.data?.errors?.[0].code === 'QCS-0401-7') {
          createDialog(
            rootUiTypes.mkRootUiData({
              id: 'community-verify-email',
              type: DIALOG_TYPE_EMPTY_DIALOG,
              allowNesting: true,
              props: ImmutableMap({
                content: (
                  <ZeroStateView
                    icon={ConfusedStarImage}
                    primary='Verify your email address'
                    secondary='We sent you an email to confirm your identity. Once verified, click here to try again.'
                  >
                    <QButton variant='contained' onClick={goToCommunity}>
                      Go to community
                    </QButton>
                  </ZeroStateView>
                ),
              }),
            }),
          );
        } else if (assert(response?.data?.redirectUri, 'no sso redirectUri')) {
          setTimeout(() => window.open(response.data.redirectUri, '_blank'), 1000); // setTimeout protects from popup blocker
        }
      })
      .catch((error) => assert(false, error));
  };

  const handleOpenLifeHub = () => {
    if (hasLifeHub) {
      tracker.track(tracker.events.qlhLaunchClicked);
      window.open('https://lifehub.quicken.com/', '_blank');
    } else {
      tracker.track(tracker.events.qlhGetStartedClicked);
      window.open('https://www.quicken.com/products/lifehub/', '_blank');
    }
  };

  const onDismissLifeHub = () => {
    tracker.track(tracker.events.qlhBannerDismissed);
    dispatch(
      preferencesV2Actions.updatePreferenceOverride(
        preferencesV2Types.mkPreferenceV2({
          id: 'sShowLifeHubBanner',
          value: false,
        }),
      ),
    );
  };

  const lifeHubPopup = (
    <>
      <Divider className={classes.divider} />
      <div
        id='lifehub-dismissable-item'
        className={classes.lifeHubContainer}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={classes.lifeHubLeft}>
          <img src={simplifiLogoLight} className={classes.logo} alt='Quicken Simplifi, click to go home' />
        </div>
        <div className={classes.lifeHubMiddle}>
          <img
            src={theme.palette.mode === 'dark' ? lifeHubLogoDark : lifeHubLogo}
            className={classes.lifeHubLogo}
            alt='Quicken LifeHub'
          />
          <Typography className={classes.lifeHubDescription} variant='captionRegular'>
            {"The smarter way to organize, protect, & share life's essential information"}
          </Typography>
          <QButton className={classes.lifeHubButton} variant='text' onClick={handleOpenLifeHub}>
            Get Started
          </QButton>
        </div>
        <div className={classes.lifeHubRight}>
          {isHovered && (
            <IconButton aria-label='Close' id='dlg-close' onClick={onDismissLifeHub} className={classes.dismissIcon}>
              <CloseRoundedIcon />
            </IconButton>
          )}
        </div>
      </div>
    </>
  );

  const showLifeHubBanner = lifeHubPref && !hasLifeHub;
  const showFinances = datasets && datasets.size > 1 && (datasets || financesEnabled);

  const financesSection = (
    <div id='dataset-change-option' className={classes.profileDataset}>
      <Typography color='textPrimary' variant='paragraphMedium'>
        Finances
      </Typography>
      <List disablePadding>
        {data &&
          data.map((theDataset, index) => (
            <ListItemButton
              key={theDataset.id}
              onClick={() => handleDatasetClick(theDataset)}
              classes={{ root: classes.datasetListItemDefault }}
              disabled={!datasetsUtils.hasActiveEntitlement(theDataset)}
            >
              <Dump obj={theDataset} />
              <ListItemIcon
                style={{
                  visibility: `${dataset === theDataset ? 'visible' : 'hidden'}`,
                  marginRight: 0,
                }}
              >
                <CheckIcon className={classes.checkStyle} />
              </ListItemIcon>
              <ListItemText
                primary={theDataset.name}
                primaryTypographyProps={{
                  variant: 'paragraphRegular',
                  color: dataset === theDataset ? theme.palette.primary.main : theme.palette.text.primary,
                }}
                id={`data-set-${index + 1}`}
              />
            </ListItemButton>
          ))}

        {datasets && datasets.size > 3 && (
          <MiniDatasetPicker
            datasets={datasets}
            dataset={dataset}
            onChange={(dataset: Dataset) => handleDatasetClick(dataset)}
            label='See all finances'
            id='mini-dataset-picker'
          />
        )}
      </List>
    </div>
  );

  return (
    <>
      <div
        id={'profile-menu-bar'}
        role={'button'}
        tabIndex={0}
        onClick={(e: SyntheticEvent) => setProfileButtonAnchorEl(e.currentTarget)}
        className={navItemClasses.navMenuItem}
      >
        <NavIconProfile className={navItemClasses.navIcon} />
        <Typography
          variant='paragraphRegular'
          style={{ marginLeft: 16, textAlign: 'left' }}
          className={classes.navText}
        >
          Profile
        </Typography>
      </div>
      <Popover
        action={popoverActions}
        id='profile-menu'
        open={!!profileButtonAnchorEl}
        anchorEl={profileButtonAnchorEl}
        onClose={handleProfileClose}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        PaperProps={{
          style: {
            width: 360,
            transform: 'translate3d(0, 0, 0)', // fixes a scroll issue: https://github.com/mui-org/@mui/material/issues/10601
          },
        }}
        onKeyDown={(e: any) => {
          if (e.altKey) {
            // can't use 'control' key because of MAC's control + click
            setShowHiddenMenu(true);
          }
          if (e.key === 'Escape') {
            handleProfileClose();
          }
        }}
        onKeyUp={() => {
          setShowHiddenMenu(false);
        }}
        disableEnforceFocus
      >
        <div className={classes.profilePopover}>
          <ProfileSection showHiddenMenu={showHiddenMenu} dataset={dataset} />
          {showFinances && financesSection}
          {showLifeHubBanner && lifeHubPopup}
          <Divider className={classes.divider} />
          <MenuList className={classes.listItems}>
            {hasLifeHub && (
              <MenuItem onClick={handleOpenLifeHub} id='open-lifehub-button'>
                <IconRowSpan
                  text='Launch Quicken LifeHub'
                  iconComponent={<OpenInNewRoundedIcon className={classes.linkIcon} />}
                  leftIcon={
                    <img
                      src={simplifiLogoLight}
                      className={classes.logo}
                      alt='Quicken LifeHub, click to open in new tab'
                    />
                  }
                />
              </MenuItem>
            )}
            {hasLifeHub && <Divider />}
            <MenuItem onClick={myAccountClick} id='my-account-button'>
              <IconRowSpan
                text='Manage Quicken ID'
                iconComponent={<OpenInNewRoundedIcon className={classes.linkIcon} />}
              />
            </MenuItem>
            {!hideFeatures && <AchievementsMenuItem closeProfile={closeProfile} />}
            {profileHelpSupport && (
              <MenuItem onClick={helpClick} id='my-account-help-and-support'>
                <Typography variant='paragraphRegular'>Help &amp; Support</Typography>
              </MenuItem>
            )}
            {intercomEnabled && (
              <MenuItem id='intercom-launcher' onClick={closeProfile}>
                <Typography variant='paragraphRegular'>Help</Typography>
              </MenuItem>
            )}

            <MenuItem id='feedback-launcher' onClick={goToCommunity}>
              <IconRowSpan text='Community' iconComponent={<OpenInNewRoundedIcon className={classes.linkIcon} />} />
            </MenuItem>

            {resetDashEnabled && (
              <MenuItem
                onClick={() => {
                  closeProfile();
                  dispatch(
                    preferencesActions.setPreference(
                      {
                        section: 'shared',
                        group: 'dataset',
                        preference: { webApp: { overviewLayoutVersion, overviewPageLayout: overviewLayoutAcme } },
                      },
                      { context: 'QPreferences:setDatasetPreference' },
                    ),
                  );
                }}
                id='reset-default-dashboard'
              >
                <Typography variant='paragraphRegular'>Reset Dashboard Layout</Typography>
              </MenuItem>
            )}

            {feedbackEnabled && showHiddenMenu && (
              <MenuItem
                onClick={() => {
                  closeProfile();
                  createDialog(
                    rootUiTypes.mkRootUiData({
                      id: 'report-error',
                      type: DIALOG_TYPE_REPORT_ERROR,
                      allowNesting: true,
                      props: ImmutableMap({
                        feedbackSubject: 'send feedback',
                      }),
                    }),
                  );
                }}
                id='send-feedback'
              >
                <Typography variant='paragraphRegular'>Send Feedback</Typography>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={logoutClick} id='my-account-sign-out'>
              <Typography variant='paragraphRegular'>Sign Out</Typography>
            </MenuItem>
          </MenuList>
        </div>
      </Popover>
    </>
  );
}

export default memo(ProfileMenu);
